import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Checkbox,
} from "@mui/material";
import { getPreneedPackages, sendQuotationToPreneedLead } from "../../../api/preneed-section/preneed-booking-appointment.api";
import { AlertComponent } from "../../../common/components";
import "./send-quotation-modal.scss";

interface Service {
  id: number;
  name: string;
  description?: string;
  serviceDescription?: string;
  price: number | string;
}

interface Category {
  id: number;
  name: string;
  services?: Service[];
}

interface Package {
  id: number;
  name: string;
  description?: string;
  price: number | string;
  isEnabled?: boolean;
  categories?: Category[];
}

interface SendQuotationModalProps {
  open: boolean;
  onClose: () => void;
  leadId: number;
  leadName: string;
  leadEmail: string;
  onSuccess?: () => void;
}

const SendQuotationModal: React.FC<SendQuotationModalProps> = ({
  open,
  onClose,
  leadId,
  leadName,
  leadEmail,
  onSuccess,
}) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<Set<number>>(new Set());
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "error" | "info" | "warning">("info");

  useEffect(() => {
    if (open) {
      fetchPackages();
      setSelectedPackages(new Set());
    }
  }, [open]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const packagesResponse = await getPreneedPackages();
      console.log("Packages response:", packagesResponse);

      if (packagesResponse?.data?.data?.data) {
        const packagesData = packagesResponse.data.data.data;

        const enabledPackages: Package[] = packagesData
          .filter((pkg: any) => pkg.isEnabled !== false)
          .map((pkg: any) => ({
            id: pkg.id,
            name: pkg.name || "",
            description: pkg.description || "",
            price: pkg.price || 0,
            isEnabled: pkg.isEnabled,
            categories: (pkg.categories || []).map((cat: any) => ({
              id: cat.id,
              name: cat.name || "",
              services: (cat.services || []).map((s: any) => ({
                id: s.id,
                name: s.name || s.serviceName || "",
                description: s.description || s.serviceDescription,
                serviceDescription: s.serviceDescription,
                price: s.price ?? s.servicePrice ?? 0,
              })),
            })),
          }));

        setPackages(enabledPackages);


        enabledPackages.forEach((pkg) => {
          fetchPackageServices(pkg.id, enabledPackages);
        });
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      setAlertMessage("Failed to fetch packages. Please try again.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageServices = async (packageId: number, packagesList?: Package[]) => {
    setLoadingServices((prev) => new Set(prev).add(packageId));
    try {

      const packagesToUse = packagesList || packages;
      const selectedPackage = packagesToUse.find((p) => p.id === packageId);

      let categoriesFromPackage: Category[] = [];

      if (!selectedPackage) {

        const packagesResponse = await getPreneedPackages();
        if (packagesResponse?.data?.data?.data) {
          const packagesData = packagesResponse.data.data.data;
          const pkg = packagesData.find((p: any) => p.id === packageId && p.isEnabled !== false);
          if (!pkg) {
            setLoadingServices((prev) => {
              const newSet = new Set(prev);
              newSet.delete(packageId);
              return newSet;
            });
            return;
          }

          categoriesFromPackage = (pkg.categories || []).map((cat: any) => ({
            id: cat.id,
            name: cat.name || "",
            services: (cat.services || []).map((s: any) => ({
              id: s.id,
              name: s.name || s.serviceName || "",
              description: s.description || s.serviceDescription,
              serviceDescription: s.serviceDescription,
              price: s.price ?? s.servicePrice ?? 0,
            })),
          }));
        } else {
          setLoadingServices((prev) => {
            const newSet = new Set(prev);
            newSet.delete(packageId);
            return newSet;
          });
          return;
        }
      } else {

        categoriesFromPackage = selectedPackage.categories || [];
      }


      setPackages((prev) =>
        prev.map((p) =>
          p.id === packageId
            ? {
              ...p,
              categories: categoriesFromPackage.map((cat) => ({
                id: cat.id,
                name: cat.name || "",
                services: (cat.services || []) as any,
              })),
            }
            : p
        )
      );
    } catch (error) {
      console.error("Error fetching package services:", error);
    } finally {
      setLoadingServices((prev) => {
        const newSet = new Set(prev);
        newSet.delete(packageId);
        return newSet;
      });
    }
  };

  const handlePackageToggle = (packageId: number) => {
    setSelectedPackages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(packageId)) {
        newSet.delete(packageId);
      } else {
        newSet.add(packageId);
      }
      return newSet;
    });
  };

  const handleSendQuotation = async () => {
    if (selectedPackages.size === 0) {
      setAlertMessage("Please select at least one package to send quotation.");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    setSending(true);
    try {

      const packageIds = Array.from(selectedPackages);
      const response = await sendQuotationToPreneedLead(leadId, {
        packageIds: packageIds,
      });

      if (response?.data?.success) {
        setAlertMessage("Quotation has been sent successfully to the lead.");
        setAlertType("success");
        setAlertOpen(true);


        setSelectedPackages(new Set());


        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
          onClose();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to send quotation");
      }
    } catch (error: any) {
      console.error("Error sending quotation:", error);
      setAlertMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send quotation. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setSending(false);
    }
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(numPrice) ? "$0.00" : `$${numPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        className="send-quotation-modal"
      >
        <Box className="send-quotation-modal__content">
          <Box className="send-quotation-modal__header">
            <Typography variant="h5" className="send-quotation-modal__title">
              Send Quotation
            </Typography>
            <Typography variant="body2" className="send-quotation-modal__subtitle">
              This quotation will be sent to the lead via email
            </Typography>
          </Box>

          <Box className="send-quotation-modal__body">
            <Box className="send-quotation-modal__recipient">
              <Typography variant="body2" className="send-quotation-modal__recipient-label">
                Sending to:
              </Typography>
              <Typography
                component="span"
                variant="body1"
                className="send-quotation-modal1__recipient-info"
              >
                <span className="send-quotation-modal__recipient-name">{leadName}</span>
                {" - "}
                <span className="send-quotation-modal__recipient-email">{leadEmail}</span>
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress size={40} />
              </Box>
            ) : packages.length === 0 ? (
              <Box p={4} textAlign="center">
                <Typography color="textSecondary">No packages available</Typography>
              </Box>
            ) : (
              <>
                {packages.map((pkg, index) => {
                  const isLoading = loadingServices.has(pkg.id);

                  return (
                    <Box key={pkg.id} className="send-quotation-modal__package-section">
                      {index > 0 && <Divider sx={{ my: 3 }} />}

                      <Box className="send-quotation-modal__package-header">
                        <Checkbox
                          checked={selectedPackages.has(pkg.id)}
                          onChange={() => handlePackageToggle(pkg.id)}
                          className="send-quotation-modal__package-checkbox"
                        />
                        <Box className="send-quotation-modal__package-name-section">
                          <Typography variant="body2" className="send-quotation-modal__package-name-label">
                            Package Name
                          </Typography>
                          <Box className="send-quotation-modal__package-name-wrapper">
                            <Typography variant="body1" className="send-quotation-modal__package-name-value">
                              {pkg.name}
                            </Typography>
                            <Typography
                              variant="body1"
                              className="send-quotation-modal__package-price-value"
                            >
                              {formatPrice(pkg.price)}
                            </Typography>
                          </Box>
                          {pkg.description ? (
                            <Typography
                              variant="body2"
                              className="send-quotation-modal__package-description"
                            >
                              {pkg.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>

                      {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : (
                        <Box className="send-quotation-modal__categories">
                          {pkg.categories && pkg.categories.length > 0 ? (
                            pkg.categories.map((category) => (
                              <Box key={category.id} className="send-quotation-modal__category-item">
                                <Typography
                                  variant="h6"
                                  className="send-quotation-modal__category-title"
                                >
                                  {category.name}
                                </Typography>
                                {category.services && category.services.length > 0 && (
                                  <Box className="send-quotation-modal__services-list">
                                    {category.services.map((service) => (
                                      <Box key={service.id} className="send-quotation-modal__service-item">
                                        <Typography variant="body1" className="send-quotation-modal__service-name">
                                          {service.name}
                                        </Typography>
                                        {((service.description ||
                                          service.serviceDescription) as
                                          | string
                                          | undefined) && (
                                            <Typography variant="body2" className="send-quotation-modal__service-description">
                                              {service.description || service.serviceDescription}
                                            </Typography>
                                          )}
                                        <Typography variant="body1" className="send-quotation-modal__service-price">
                                          {formatPrice(service.price)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            ))
                          ) : null}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </>
            )}
          </Box>

          <Box className="send-quotation-modal__footer">
            <Typography variant="caption" className="send-quotation-modal__footer-note">
              Quotation will be sent email summary
            </Typography>
            <Box className="send-quotation-modal__footer-actions">
              <Button
                variant="outlined"
                onClick={onClose}
                className="send-quotation-modal__cancel-btn"
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendQuotation}
                className="send-quotation-modal__send-btn"
                disabled={sending || selectedPackages.size === 0}
              >
                {sending ? "Sending..." : "Send Quotation"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default SendQuotationModal;
